import React, { useMemo, useRef, useState } from "react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { Tooltip as TooltipEllips } from "src/assets/imports/antdComponents";
import { ChartGridType, chartType, ChartViewType, IDynamicChartsProps } from "src/pages/Insights/models/interfaces";
import { formatValue, renderNValueLabel, sortByValuesAndThenByMax } from "src/pages/Insights/transformer";
import { CustomMarker } from "src/components/Charts/DynamicCharts/components/CustomMarker";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { chartStyleConfigLine } from "src/pages/Insights/transformer";
import { CustomTooltip } from "src/components/Charts/DynamicCharts/components/CustomTooltip";
import AxisLabel from "../../../BarChart/components/AxisLable";
import { QuantComponentsRenderedFromType } from "src/pages/NewAskKural/models/interfaces";
import useAsyncChartConfig from "src/components/Hooks/useAsyncChartConfig";

export const LineVertical: React.FC<IDynamicChartsProps> = ({
	obj = {},
	type,
	config,
	isMini,
	legends = [],
	showYAxisLabel = true,
	showXAxis = true,
	maxDomain = "auto",
	minDomain = "auto",
	chartSegmentLength = 1, // Add this prop
	isSectionalSummary = false,
	renderedFrom = QuantComponentsRenderedFromType.Quant,
	viewType = ChartViewType.Dashboard,
	grid = ChartGridType.oneXone,
}) => {
	if (!obj) return null;
	const [hoveredLineKey, setHoveredLineKey] = useState<string | null>(null);
	const { selectedChart } = useSelector((state: RootState) => state.sectionalSummarySlice.sectionalSummary);
	const { n_size_value = [] } = obj;

	const [dynamicPosition, setDynamicPosition] = useState(0);
	const [isTooltipHovered, setIsTooltipHovered] = useState(false);

	const bottomMargin = obj?.xAxisLables ? (obj?.graph_customization?.legend_pos !== "Top" ? 10 : 50) : obj?.graph_customization?.legend_pos !== "Top" ? 50 : 50;

	const data2 =
		legends?.length > 0
			? obj?.chartData?.map((item) => {
					return Object.fromEntries(Object.entries(item).filter(([key]) => !legends.includes(key)));
			  })
			: obj?.chartData;

	const data = data2;

	const indexX = useRef(-1);
	const item = Object.keys(data?.[0] || {})[1];
	// const barKeys = Object.keys(data?.[0] || {}).filter((key) => key !== config.dataKeyXAxis);
	const barKeys: string[] = data ? [...new Set((data as { [key: string]: any }[]).flatMap((obj) => Object.keys(obj).filter((key) => key !== config.dataKeyXAxis)))] : [];

	const { config: customConfig } = useAsyncChartConfig(obj?.chartType, viewType, grid);

	const fontCustom = chartStyleConfigLine?.yAxisLabelSize;
	// obj?.chartData?.length <= 2
	// 	? chartStyleConfigLine?.yAxisLabelSize
	// 	: obj?.chartData?.length <= 8
	// 	? chartStyleConfigLine?.yAxisLabelSize - 1
	// 	: chartStyleConfigLine?.yAxisLabelSize - 2;
	const CustomXAxisTick = (props) => {
		let showToolTip = true;
		const { x, y, payload } = props;
		const maxWidth = customConfig?.x_axis_bottom?.tick?.max_width;
		const format = obj?.graph_customization?.dlabel_fmt || "##";
		const value = formatValue(payload.value, format);
		let truncatedText = value;
		// const format = (obj.graph_customization?.dlabel_fmt || "##").replace(/\.\#+/, "");
		const getEllipsisText = (str, maxLength) => {
			const canvas = document.createElement("canvas");
			const context = canvas.getContext("2d");
			context.font = `${isMini ? 8 : fontCustom}px Open Sans`;

			while (context.measureText(truncatedText + "...").width > maxWidth && truncatedText.length > 0) {
				truncatedText = truncatedText.slice(0, -1);
			}
			if (truncatedText.length < str.length) {
				showToolTip = true;
			} else {
				showToolTip = false;
			}

			return truncatedText.length < str.length ? truncatedText + "..." : str;
		};

		const displayValue = getEllipsisText(value, maxWidth);

		return (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={type?.toLowerCase() === chartType.Bar?.toLowerCase() ? -30 : -30} y={10} width={60} height={20}>
					{showToolTip ? (
						<TooltipEllips title={value} overlayClassName="quant-tooltip-reduced-padding">
							<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
								<span
									style={{
										fontSize: customConfig?.x_axis_bottom?.tick?.font_size,
										color: customConfig?.x_axis_bottom?.tick?.label_color,
										fontWeight: customConfig?.x_axis_bottom?.tick?.font_weight,
									}}>
									{/* {getEllipsisText(value, maxWidth)} */}
									{displayValue}
								</span>
							</div>
						</TooltipEllips>
					) : (
						<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<span
								style={{
									fontSize: customConfig?.x_axis_bottom?.tick?.font_size,
									color: customConfig?.x_axis_bottom?.tick?.label_color,
									fontWeight: customConfig?.x_axis_bottom?.tick?.font_weight,
								}}>
								{/* {getEllipsisText(value, maxWidth)} */}
								{displayValue}
							</span>
						</div>
					)}
				</foreignObject>
			</g>
		);
	};

	const CustomXAxisTickLineVertical = (props) => {
		let showToolTip = true;
		const { x, y, payload, allLabels } = props;

		const text = payload?.value;
		// const format = (obj.graph_customization?.dlabel_fmt || "##").replace(/\.\#+/, "");
		const format = obj?.graph_customization?.dlabel_fmt || "##";

		// Dynamically calculate max width based on the longest label
		const calculateMaxWidth = () => {
			if (isMini) return 20;

			// If isChartCompare is true, force width to 50px
			if (chartSegmentLength >= 2) return 140;

			if (!allLabels || allLabels.length === 0) {
				return isMini ? 20 : 100; // Keep original values for normal mode
			}

			let maxLabelLength = 0;
			if (Array.isArray(allLabels)) {
				maxLabelLength = Math.max(...allLabels.map((label) => label?.length ?? 0));
			}
			const minWidth = isMini ? 15 : 40; // Keep original values for normal mode
			// Keep original max possible width for normal mode
			const maxPossibleWidth = isMini ? 40 : 180;

			const calculatedWidth = Math.min(Math.max(maxLabelLength * 5 + 10, 100), maxPossibleWidth); // Keep original multiplier
			return Math.max(calculatedWidth, minWidth);
		};
		const maxWidth = calculateMaxWidth();
		const xPosition = isMini ? -10 : chartSegmentLength >= 2 ? -110 : selectedChart ? Math.min(-maxWidth - 150, -0) : Math.min(-maxWidth - 80, -0);
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		setDynamicPosition(xPosition);

		// Get two-line label with ellipsis if needed
		const getTwoLineText = (str, maxWidth) => {
			if (typeof document === "undefined") return { line1: str, line2: "" };

			ctx.font = "11px Open Sans";
			let words = String(str).split(" ");
			let line1 = "";

			if (isNaN(payload?.value)) {
				// More aggressive truncation when isChartCompare is true
				const widthThreshold = chartSegmentLength >= 2 ? maxWidth * 0.6 : maxWidth * 0.9;

				for (let i = 0; i < words.length; i++) {
					let testLine1 = line1 + (line1 ? " " : "") + words[i];
					if (ctx.measureText(testLine1).width <= widthThreshold) {
						line1 = testLine1;
						showToolTip = false;
					} else {
						if (line1.length === 0) {
							// If even the first word is too long, truncate it more aggressively in compare mode
							const charLimit = chartSegmentLength >= 2 ? Math.floor(maxWidth / 12) : Math.floor(maxWidth / 8);
							line1 = words[i].substring(0, charLimit) + "...";
						} else {
							line1 = line1 + "...";
						}
						showToolTip = true;
						break;
					}
				}
			} else {
				line1 = formatValue(payload.value, format);
			}

			return { line1, line2: "" };
		};

		const { line1, line2 } = getTwoLineText(text, maxWidth);

		return showToolTip ? (
			<TooltipEllips title={text} overlayClassName="quant-tooltip-reduced-padding">
				<g transform={`translate(${x},${y})`}>
					<text
						// x={isMini ? 0 : xPosition + 0}
						x={0}
						y={0}
						fill={customConfig?.y_axis_left?.tick?.label_color}
						textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
						fontSize={customConfig?.y_axis_left?.tick?.font_size}
						fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
						<tspan
							x={chartSegmentLength >= 2 ? xPosition + 15 : selectedChart ? xPosition + 120 : xPosition + 60}
							dy="0em"
							fill={customConfig?.y_axis_left?.tick?.label_color}>
							{line1}
						</tspan>
						{/* <tspan x={xPosition} dy="1.2em">
										{line2}
									</tspan> */}
					</text>
				</g>
			</TooltipEllips>
		) : (
			<g transform={`translate(${x},${y})`}>
				<text
					// x={isMini ? 0 : xPosition + 0}
					x={0}
					y={0}
					fill={customConfig?.y_axis_left?.tick?.label_color}
					textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
					fontSize={customConfig?.y_axis_left?.tick?.font_size}
					fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
					<tspan
						x={chartSegmentLength >= 2 ? xPosition + 15 : selectedChart ? xPosition + 120 : xPosition + 60}
						dy="0em"
						fill={customConfig?.y_axis_left?.tick?.label_color}>
						{line1}
					</tspan>
				</text>
			</g>
		);
	};

	// const CustomYAxisTickRight = (props, i) => {
	// 	const { x, y, payload, index } = props;
	// 	indexX.current = indexX.current + 1;
	// 	return (
	// 		<g transform={`translate(${x},${y})`}>
	// 			<text x={type?.toLowerCase() === "bar" ? 50 : 55} y={5} fill="#7c9dda" textAnchor="middle" fontSize="11px" fontWeight="400" style={{ color: "red" }}>
	// 				{n_size_value?.[index]}
	// 			</text>
	// 		</g>
	// 	);
	// 	// }
	// };
	const CustomYAxisTickRight = (props) => {
		const { x, y, payload, index } = props;
		indexX.current = indexX.current + 1;
		const l = n_size_value?.length;

		const h = index == !0 ? 0 : 20;
		const parsed = renderNValueLabel(n_size_value?.[index], customConfig?.y_axis_right?.tick?.label_color, customConfig?.y_axis_right?.tick?.label_color_dark);
		// This is the background for each individual tick
		return (
			<g transform={`translate(${x - 40},${y})`}>
				{/* Background rectangle for this specific tick */}
				{/* <rect
					x={10}
					// x={type?.toLowerCase() === "bar" ? 30 : 35}
					y={-10 - 2 * h}
					width={100}
					height={index === n_size_value?.length - 1 ? 0 : selectedChart ? 600 / l + h : 300 / l + h}
					// fill="#f5f5f5" // Light red background for each tick
					fill="#fff" // Light red background for each tick
					// rx={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0} // Round if top or bottom
					// ry={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0}
				/> */}

				{/* Text displaying the n_size value */}
				<text
					x={type?.toLowerCase() === "bar" ? 50 : 70}
					y={5}
					fill="#d32f2f" // Darker red for text renderNValueLabel
					textAnchor={customConfig?.y_axis_right?.tick?.text_anchor}
					fontSize={customConfig?.y_axis_right?.tick?.font_size}
					fontWeight={customConfig?.y_axis_right?.tick?.font_weight}
					style={{ userSelect: "none" }}>
					{parsed?.labelParts?.map((part, idx) => (
						<tspan key={idx} fill={part.color}>
							{part.text}
						</tspan>
					))}
				</text>
			</g>
		);
	};

	const CustomLabel = useMemo(() => {
		return (props) => {
			const { x, y, value, index, dataKey } = props;

			if (value === null || value === undefined || isNaN(Number(value))) {
				return null;
			}

			const isFirstOrLast = index === 0 || index === data.length - 1;
			if (!isFirstOrLast) return null;
			const format = obj?.graph_customization?.dlabel_fmt || "##";
			const valueFormated = formatValue(Number(value), format);
			return (
				<text
					y={index === 0 ? y - 10 : index === data.length - 1 ? y + 14 : y}
					x={x}
					textAnchor={customConfig?.resolved_data_label?.text_anchor}
					fill={obj?.colorLegends?.[dataKey] || customConfig?.resolved_data_label?.label_color}
					fontSize={obj?.graph_customization?.dlabel_size ? `${obj?.graph_customization.dlabel_size}px` : `${customConfig?.resolved_data_label?.font_size}px`}
					fontWeight={customConfig?.resolved_data_label?.font_weight}
					onMouseMove={() => setHoveredLineKey(dataKey)}>
					{valueFormated}
				</text>
			);
		};
	}, [data, obj, setHoveredLineKey]);

	function getBottomPosition() {
		if (isMini) return 0;
		if (isSectionalSummary) return 0;
		const legendPosition = obj?.graph_customization?.legend_pos;
		if (legendPosition === "Top") {
			if (barKeys?.length === 1 && config.layout === "vertical") return 10;
			if (barKeys?.length === 1 && config.layout === "horizontal") return 20;
			return obj?.xAxisLables ? 80 : 60;
		}
		if (config.layout !== "vertical") return 15;
		if (barKeys?.length === 1 && config.layout === "vertical") return 10;
		return 80 - bottomMargin;
	}

	function getLeftPosition() {
		const m = customConfig?.margin?.left;

		if (isMini) return m?.default;
		if (!showYAxisLabel) return m?.no_y_axis_label;
		if (chartSegmentLength >= m?.multi_segment_threshold) {
			return m.multi_segment_value;
		}

		// then apply dynamic offset
		const offsetKey = selectedChart ? "selected_chart" : "default";
		return -dynamicPosition - m?.dynamic_offset[offsetKey];
	}

	const chartMargins = useMemo(() => {
		// if (renderedFrom === QuantComponentsRenderedFromType.Widget) {
		// 	return {
		// 		left: isMini ? 10 : !showYAxisLabel ? 50 : chartSegmentLength >= 2 ? 50 : selectedChart ? -dynamicPosition - 150 : -dynamicPosition - 100,
		// 		right: isMini ? 20 : 50,
		// 		top: 20,
		// 		bottom: getBottomPosition(),
		// 	};
		// }
		return {
			left: getLeftPosition(),
			right: customConfig?.margin?.right,
			top: customConfig?.margin?.top,
			bottom: getBottomPosition(),
		};
	}, [isMini, getBottomPosition, renderedFrom, showYAxisLabel, chartSegmentLength, selectedChart, dynamicPosition, customConfig?.margin]);

	return (
		<>
			<div className="flex">
				{obj?.yAxisLables && !isMini && showYAxisLabel && (
					<div className="flex justify-center items-center max-w-4" style={{ paddingBottom: "50px" }}>
						<AxisLabel title={obj?.yAxisLables} type="y" config={customConfig?.y_axis_label} />
					</div>
				)}

				<ResponsiveContainer height={config.height ?? 250}>
					<LineChart width={config.width} height={config.height} data={data} margin={chartMargins} layout="vertical">
						<CartesianGrid
							//strokeDasharray="3 3"
							stroke={customConfig?.cartesian_grid?.stroke}
							horizontal={config?.h_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_h_grid}
							vertical={config?.v_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_v_grid}
							strokeWidth={customConfig?.cartesian_grid?.stroke_width}
						/>

						<XAxis
							type="number"
							hide={isMini || !showXAxis || obj?.graph_customization?.x_vis?.toLocaleLowerCase() !== "yes"}
							tick={<CustomXAxisTick />}
							tickLine={customConfig?.x_axis_bottom?.show_tick_line}
							domain={[0, maxDomain]}
							stroke={customConfig?.x_axis_bottom?.stroke}
							strokeWidth={customConfig?.x_axis_bottom?.stroke_width}
						/>
						<YAxis
							dataKey={config.dataKeyXAxis}
							padding={{ bottom: customConfig?.y_axis_left?.padding?.bottom }}
							yAxisId="left"
							orientation="left"
							type="category" // Should be category
							hide={isMini || !showYAxisLabel || obj?.graph_customization?.y_vis?.toLocaleLowerCase() !== "yes"}
							tickLine={customConfig?.y_axis_left?.show_tick_line}
							//axisLine={false}
							tick={(props) => (
								<CustomXAxisTickLineVertical
									{...props}
									allLabels={data?.map((item) => item?.name)}
									isMini={isMini} // if you're using isMini
								/>
							)} //
							interval={customConfig?.y_axis_left.interval}
							dx={isMini ? 50 : -10}
							stroke={customConfig?.y_axis_left.stroke} //#4f81bc
							// stroke={y_axis_color} //#4f81bc//y_axis_color
							strokeWidth={customConfig?.y_axis_left.stroke_width}
							domain={[0, "auto"]}
						/>

						<YAxis
							dataKey={config.dataKeyXAxis}
							padding={{ bottom: customConfig?.y_axis_right?.padding?.bottom }}
							yAxisId="right"
							orientation="right"
							type="category" // Should be category
							//hide={isMini ? true : obj.graph_customization?.y_vis?.toLocaleLowerCase() !== "yes"}
							// hide={isMini || obj.graph_customization?.y_vis?.toLocaleLowerCase() !== "yes" || barKeys?.length === 1}
							hide={isMini}
							tickLine={customConfig?.y_axis_right?.show_tick_line}
							axisLine={customConfig?.y_axis_right?.show_axis_line}
							tick={<CustomYAxisTickRight />}
							// label={{ value: isMini ? "n size" : "n size", angle: 0, position: "top", dy: -5, dx: 30, style: { fontSize: "11px", fontWeight: 500 } }} //obj?.yAxisLables
							interval={customConfig?.y_axis_right?.interval}
							stroke={customConfig?.y_axis_right?.stroke}
							strokeWidth={customConfig?.y_axis_right?.stroke_width}
							dx={100}
						/>

						{config.showTooltip && !isMini && (
							<Tooltip
								content={
									<CustomTooltip
										chartDataTooltip={obj?.chartDataTooltip}
										graphCustomization={obj?.graph_customization}
										setIsTooltipHovered={setIsTooltipHovered}
										type={"custom"}
										hoveredBarKey={hoveredLineKey}
										customConfig={customConfig}
									/>
								}
								cursor={{ fill: customConfig?.tooltip?.color, x1: -100 }}
								//followCursor={!isTooltipHovered}
							/>
						)}
						{config.showLegends && <Legend />}
						{barKeys.map((key, index) => {
							return (
								<Line
									yAxisId={"right"}
									key={key}
									strokeWidth={1}
									type="linear"
									dataKey={key}
									stroke={obj?.colorLegends?.[key]}
									activeDot={{ r: chartStyleConfigLine?.activeDotSize }}
									onMouseMove={() => setHoveredLineKey(key)}
									connectNulls={false}
									// onMouseLeave={() => setHoveredLineKey(null)}
									dot={(props) => {
										const value = props.payload[props.dataKey];
										if (value === null || value === undefined || isNaN(Number(value))) {
											return null;
										}
										return (
											<CustomMarker
												{...props}
												markerType={obj.graph_customization?.lc_marker_sp?.[key]}
												markerSize={obj.graph_customization?.line_marker_size?.[key] || 8}
												isMini={isMini}
												onMouseEnter={() => setHoveredLineKey(key)}
											/>
										);
									}}>
									{config?.show_dlabels && !isMini && <LabelList dataKey={key} content={(props) => <CustomLabel {...props} dataKey={key} />} />}
								</Line>
							);
						})}
					</LineChart>
				</ResponsiveContainer>
				{/* {!isMini && (
					<div className="custom-y-axis-right bg-red-200">
						{n_size_value?.map((value) => {
							return <div>{value}</div>;
						})}
					</div>
				)} */}
			</div>
			{obj?.xAxisLables && !isMini && (
				<div className="flex justify-center items-center -mt-8 max-h-4">
					<AxisLabel title={obj?.xAxisLables} type="x" config={customConfig?.x_axis_label} />
				</div>
			)}
		</>
	);
};
